/*
 * Copyright (c) 2018-Present, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *
 * See the License for the specific language governing permissions and limitations under the License.
 */

import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useOktaAuth } from '@okta/okta-react';

interface Props {
  authRequiredModalOpen: boolean;
  setAuthRequiredModalOpen: (open: boolean) => void;
}

/**
 * refactored for MUI added typescript
 * @param authRequiredModalOpen
 * @param setAuthRequiredModalOpen
 * @constructor
 */
function AuthRequiredModal({ authRequiredModalOpen, setAuthRequiredModalOpen }: Props) {
  const { t } = useTranslation();
  const { oktaAuth } = useOktaAuth();
  const closeModal = () => {
    setAuthRequiredModalOpen(false);
  };

  const confirmModal = () => {
    setAuthRequiredModalOpen(false);
    oktaAuth.signInWithRedirect();
  };

  const noBackdropClose = (reason: string): void => {
    if (reason !== 'backdropClick') {
      closeModal();
    }
  };

  return (
    <Modal
      open={authRequiredModalOpen}
      onClose={(_: unknown, reason: 'backdropClick' | 'escapeKeyDown') => noBackdropClose}
    >
      <Box>
        <Typography variant='h1' className='h5'>
          {t('auth.authRequired')}
        </Typography>
        <Box>
          <Typography variant='body1'> {t('auth.reAuthenticate')}</Typography>
        </Box>
        <Box>
          <Button onClick={closeModal}>{t('buttonText.no')}</Button>
          <Button onClick={confirmModal}>{t('buttonText.yes')}</Button>
        </Box>
      </Box>
    </Modal>
  );
}
export default AuthRequiredModal;
